import { FullPageLoader } from "@/components/FullPageLoader";
import { useEffect, useState } from "react";
import { Loader2 } from "lucide-react";
import { HtmlCoursePlayer } from "@/components/learning/HtmlCoursePlayer";
import { invokeFunction } from "@/lib/invokeFunction";

interface Props {
  activityId: string;
  inlineRawHtml?: string;
  storageKey?: string;
  // Pass-through props for HtmlCoursePlayer (kept identical)
  courseTitle: string;
  courseId: string;
  onComplete: () => void;
  isCompleting: boolean;
  isAlreadyCompleted: boolean;
  initialVisitedPages: number[];
  initialCurrentPage?: number;
  initialMaxPageReached: number;
  initialScrollProgress: number;
  justCompleted: boolean;
  onViewCertificate: () => void;
  onProgressChange: React.ComponentProps<typeof HtmlCoursePlayer>["onProgressChange"];
}

/**
 * Resolves the HTML string for an html_upload iframe course from either
 * inline content.rawHtml (legacy / un-migrated) or content.storageKey
 * (migrated to the "course-html" bucket via a 5-min signed URL from the
 * get-course-content-url edge function). It then hands the resolved HTML
 * string to <HtmlCoursePlayer/> exactly as before — the player itself is
 * unchanged.
 */
export function HtmlCourseViewLoader(props: Props) {
  const { activityId, inlineRawHtml, storageKey } = props;

  // Fast path: inline rawHtml — no fetch, identical to previous behaviour.
  const hasInline = typeof inlineRawHtml === "string" && inlineRawHtml.length > 0;

  const [resolvedHtml, setResolvedHtml] = useState<string | null>(
    hasInline ? (inlineRawHtml as string) : null,
  );
  const [loadError, setLoadError] = useState<string | null>(null);

  useEffect(() => {
    if (hasInline) return; // no fetch needed
    if (!storageKey) {
      setLoadError("This course has no content available.");
      return;
    }

    let cancelled = false;
    (async () => {
      setLoadError(null);
      setResolvedHtml(null);
      try {
        const { data, error } = await invokeFunction<{ url: string }>(
          "get-course-content-url",
          { body: { activityId } },
        );
        if (error || !data?.url) {
          throw new Error(error?.message || "Failed to get signed URL");
        }
        const res = await fetch(data.url);
        if (!res.ok) {
          throw new Error(`Storage fetch failed (${res.status})`);
        }
        const text = await res.text();
        if (!cancelled) setResolvedHtml(text);
      } catch (e: any) {
        console.error("[HtmlCourseViewLoader] failed to load course html:", e);
        if (!cancelled) {
          setLoadError(
            e?.message
              ? `Unable to load course content: ${e.message}`
              : "Unable to load course content.",
          );
        }
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [activityId, storageKey, hasInline]);

  if (loadError) {
    return (
      <div className="flex min-h-[60vh] flex-col items-center justify-center gap-3 p-6 text-center">
        <p className="text-lg font-medium text-foreground">
          Unable to load course content
        </p>
        <p className="max-w-md text-sm text-muted-foreground">{loadError}</p>
      </div>
    );
  }

  if (resolvedHtml === null) {
    return (
      <FullPageLoader />
    );
  }

  return (
    <HtmlCoursePlayer
      courseTitle={props.courseTitle}
      courseId={props.courseId}
      rawHtml={resolvedHtml}
      onComplete={props.onComplete}
      isCompleting={props.isCompleting}
      isAlreadyCompleted={props.isAlreadyCompleted}
      initialVisitedPages={props.initialVisitedPages}
      initialCurrentPage={props.initialCurrentPage}
      initialMaxPageReached={props.initialMaxPageReached}
      initialScrollProgress={props.initialScrollProgress}
      justCompleted={props.justCompleted}
      onViewCertificate={props.onViewCertificate}
      onProgressChange={props.onProgressChange}
    />
  );
}
